import { MealPlan } from '@/lib/index';

// ── Multi-Store Demo Meal Plans ───────────────────────────────────────────────
// Each chain has its own curated meal plan reflecting that store's typical
// weekly specials, price points, and product range.
// These are used as fallback when the Flipp API is unavailable.

export const SOBEYS_MEAL_PLAN: MealPlan = {
  store_name: 'Sobeys',
  week_summary: '7 meals built from this week\'s Sobeys flyer — featuring Atlantic salmon, boneless chicken thighs, and fresh Ontario produce on sale.',
  demo_mode: true,
  sale_items_found: 98,
  savings_vs_regular: 34.20,
  total_grocery_cost: 71.40,
  total_grocery_cost_no_spices: 58.90,
  meals: [
    {
      id: 'sobeys-1',
      day: 'Monday', meal_type: 'Dinner',
      name: 'Lemon Herb Baked Salmon',
      tagline: 'Atlantic salmon fillet on sale — rich, flaky, and done in 20 min',
      serves: 2, cook_time: '25 min', method: 'Bake',
      total_cost: 12.40, per_serving: 6.20,
      ingredients: [
        { name: 'Atlantic Salmon Fillet (Sobeys – on sale)', qty: '2 fillets (300g)', price: 7.99, on_sale: true },
        { name: 'Lemon', qty: '1', price: 0.69, on_sale: false },
        { name: 'Fresh Dill', qty: '1 bunch', price: 1.99, on_sale: true },
        { name: 'Garlic', qty: '2 cloves', price: 0.30, on_sale: false },
        { name: 'Olive oil, salt, pepper', qty: 'pantry', price: 0.00, on_sale: false },
      ],
      instructions: [
        'Preheat oven to 400°F.',
        'Place salmon on a lined baking sheet. Drizzle with olive oil.',
        'Top with sliced lemon, minced garlic, and fresh dill.',
        'Season generously with salt and pepper.',
        'Bake 18–20 min until salmon flakes easily. Serve immediately.',
      ],
      tip: 'Don\'t overcook — salmon should still be slightly translucent in the very centre for best texture.',
    },
    {
      id: 'sobeys-2',
      day: 'Tuesday', meal_type: 'Dinner',
      name: 'Chicken Thigh Cacciatore',
      tagline: 'Bone-in chicken thighs simmered in tomato, peppers, and olives',
      serves: 4, cook_time: '55 min', method: 'Stovetop',
      total_cost: 14.80, per_serving: 3.70,
      ingredients: [
        { name: 'Bone-In Chicken Thighs (Sobeys – on sale)', qty: '1.2 kg', price: 6.99, on_sale: true },
        { name: 'Roma Tomatoes', qty: '4', price: 1.99, on_sale: true },
        { name: 'Bell Pepper (red)', qty: '1', price: 1.49, on_sale: true },
        { name: 'Kalamata Olives', qty: '½ cup', price: 1.99, on_sale: false },
        { name: 'Onion, garlic, Italian seasoning', qty: 'pantry', price: 0.00, on_sale: false },
      ],
      instructions: [
        'Season chicken with salt, pepper, and Italian seasoning.',
        'Brown in olive oil over medium-high heat, 4 min per side.',
        'Remove chicken. Sauté onion and garlic in same pan.',
        'Add chopped tomatoes, peppers, and olives. Cook 5 min.',
        'Return chicken to pan, cover and simmer 35 min.',
      ],
      tip: 'Serve over pasta or crusty bread to soak up the rich tomato sauce.',
    },
    {
      id: 'sobeys-3',
      day: 'Wednesday', meal_type: 'Dinner',
      name: 'Ground Turkey Tacos',
      tagline: 'Lean ground turkey with house-made seasoning — quick weeknight win',
      serves: 4, cook_time: '20 min', method: 'Stovetop',
      total_cost: 11.60, per_serving: 2.90,
      ingredients: [
        { name: 'Lean Ground Turkey (Sobeys – on sale)', qty: '500g', price: 5.99, on_sale: true },
        { name: 'Taco Shells (pkg of 12)', qty: '12', price: 2.99, on_sale: true },
        { name: 'Shredded Tex-Mex Cheese', qty: '1 cup', price: 2.49, on_sale: false },
        { name: 'Lime, onion, cumin, chili powder', qty: 'pantry', price: 0.00, on_sale: false },
      ],
      instructions: [
        'Brown ground turkey in a skillet over medium-high heat.',
        'Season with cumin, chili powder, garlic powder, salt.',
        'Fill taco shells with turkey, top with cheese and lime juice.',
      ],
      tip: 'Add avocado slices and hot sauce for extra flavour.',
    },
    {
      id: 'sobeys-4',
      day: 'Thursday', meal_type: 'Dinner',
      name: 'Pork Tenderloin with Apple Slaw',
      tagline: 'Juicy pork paired with crisp Gala apple slaw — only 30 min total',
      serves: 3, cook_time: '30 min', method: 'Sear + Roast',
      total_cost: 13.20, per_serving: 4.40,
      ingredients: [
        { name: 'Pork Tenderloin (Sobeys – on sale)', qty: '500g', price: 6.99, on_sale: true },
        { name: 'Gala Apples', qty: '2', price: 1.80, on_sale: true },
        { name: 'Cabbage (pre-shredded bag)', qty: '½ bag', price: 1.99, on_sale: false },
        { name: 'Apple cider vinegar, Dijon, honey', qty: 'pantry', price: 0.00, on_sale: false },
      ],
      instructions: [
        'Season tenderloin with salt, pepper, garlic powder.',
        'Sear in oven-safe pan over high heat, all sides, 2 min each.',
        'Transfer to 425°F oven for 15 min until internal temp 145°F.',
        'Meanwhile, toss shredded cabbage and julienned apple with vinegar, Dijon, and honey.',
        'Slice tenderloin and serve with apple slaw.',
      ],
      tip: 'Rest pork 5 min before slicing so juices redistribute.',
    },
    {
      id: 'sobeys-5',
      day: 'Friday', meal_type: 'Dinner',
      name: 'Shrimp Stir-Fry with Bok Choy',
      tagline: 'Frozen cooked shrimp + bok choy + oyster sauce — restaurant speed at home',
      serves: 2, cook_time: '15 min', method: 'Wok',
      total_cost: 10.80, per_serving: 5.40,
      ingredients: [
        { name: 'Frozen Cooked Shrimp (Sobeys – on sale)', qty: '300g', price: 5.99, on_sale: true },
        { name: 'Baby Bok Choy', qty: '4 heads', price: 2.49, on_sale: true },
        { name: 'Oyster sauce, sesame oil, soy sauce', qty: 'pantry', price: 0.00, on_sale: false },
        { name: 'Instant rice', qty: '2 cups cooked', price: 0.80, on_sale: false },
      ],
      instructions: [
        'Thaw shrimp under cold running water 5 min.',
        'Heat wok over high heat. Add sesame oil.',
        'Stir-fry bok choy 2 min until wilted.',
        'Add shrimp, oyster sauce, and splash of soy sauce. Toss 2 min.',
        'Serve over steamed rice.',
      ],
      tip: 'A very hot wok is essential for good stir-fry char. Don\'t crowd the pan.',
    },
    {
      id: 'sobeys-6',
      day: 'Saturday', meal_type: 'Lunch',
      name: 'Hearty Lentil Soup',
      tagline: 'Budget-friendly lentils with carrots and celery — makes a full pot',
      serves: 6, cook_time: '40 min', method: 'Stovetop',
      total_cost: 7.40, per_serving: 1.23,
      ingredients: [
        { name: 'Green Lentils (dry, bulk)', qty: '1.5 cups', price: 2.49, on_sale: false },
        { name: 'Carrots', qty: '3', price: 1.20, on_sale: true },
        { name: 'Celery', qty: '3 stalks', price: 0.80, on_sale: false },
        { name: 'Diced Tomatoes (can)', qty: '1 × 796ml', price: 1.49, on_sale: true },
        { name: 'Onion, garlic, cumin, vegetable broth', qty: 'pantry', price: 0.00, on_sale: false },
      ],
      instructions: [
        'Sauté onion and garlic in olive oil until soft.',
        'Add chopped carrots and celery. Cook 3 min.',
        'Add rinsed lentils, diced tomatoes, and 5 cups vegetable broth.',
        'Season with cumin, salt, and pepper.',
        'Simmer 30 min until lentils are tender.',
      ],
      tip: 'Freeze portions in 2-cup containers for easy weekday lunches.',
    },
    {
      id: 'sobeys-7',
      day: 'Sunday', meal_type: 'Dinner',
      name: 'Beef & Vegetable Pot Roast',
      tagline: 'Classic Sunday pot roast with root vegetables — slow and easy',
      serves: 6, cook_time: '3 hrs', method: 'Slow Roast',
      total_cost: 22.40, per_serving: 3.73,
      ingredients: [
        { name: 'Beef Chuck Roast (Sobeys – on sale)', qty: '1.5 kg', price: 14.99, on_sale: true },
        { name: 'Yukon Gold Potatoes', qty: '500g', price: 2.49, on_sale: true },
        { name: 'Baby Carrots (bag)', qty: '1 lb bag', price: 2.49, on_sale: false },
        { name: 'Beef broth, rosemary, thyme', qty: 'pantry', price: 0.00, on_sale: false },
      ],
      instructions: [
        'Preheat oven to 325°F.',
        'Season chuck roast generously on all sides.',
        'Sear in Dutch oven over high heat, all sides.',
        'Add potatoes, carrots, beef broth, and herbs.',
        'Cover tightly and roast 2.5–3 hrs until beef falls apart.',
      ],
      tip: 'The longer and lower the cook, the more tender. Don\'t rush this one.',
    },
  ],
  grocery_list: [
    {
      section: '🐟 Seafood',
      items: [
        { name: 'Atlantic Salmon Fillet', qty: '300g', price: 7.99, used_in: 'Lemon Herb Baked Salmon' },
        { name: 'Frozen Cooked Shrimp', qty: '300g', price: 5.99, used_in: 'Shrimp Stir-Fry' },
      ],
    },
    {
      section: '🥩 Meat & Poultry',
      items: [
        { name: 'Bone-In Chicken Thighs', qty: '1.2 kg', price: 6.99, used_in: 'Chicken Cacciatore' },
        { name: 'Lean Ground Turkey', qty: '500g', price: 5.99, used_in: 'Ground Turkey Tacos' },
        { name: 'Pork Tenderloin', qty: '500g', price: 6.99, used_in: 'Pork with Apple Slaw' },
        { name: 'Beef Chuck Roast', qty: '1.5 kg', price: 14.99, used_in: 'Pot Roast' },
      ],
    },
    {
      section: '🥦 Produce',
      items: [
        { name: 'Roma Tomatoes', qty: '4', price: 1.99, used_in: 'Chicken Cacciatore' },
        { name: 'Bell Pepper (red)', qty: '1', price: 1.49, used_in: 'Chicken Cacciatore' },
        { name: 'Baby Bok Choy', qty: '4 heads', price: 2.49, used_in: 'Shrimp Stir-Fry' },
        { name: 'Gala Apples', qty: '2', price: 1.80, used_in: 'Pork with Apple Slaw' },
        { name: 'Carrots', qty: '3', price: 1.20, used_in: 'Lentil Soup' },
        { name: 'Yukon Gold Potatoes', qty: '500g', price: 2.49, used_in: 'Pot Roast' },
        { name: 'Fresh Dill', qty: '1 bunch', price: 1.99, used_in: 'Lemon Herb Baked Salmon' },
        { name: 'Lemon', qty: '1', price: 0.69, used_in: 'Salmon' },
      ],
    },
    {
      section: '🥫 Pantry & Canned',
      items: [
        { name: 'Diced Tomatoes (796ml)', qty: '1 can', price: 1.49, used_in: 'Lentil Soup' },
        { name: 'Taco Shells (pkg 12)', qty: '1 pkg', price: 2.99, used_in: 'Ground Turkey Tacos' },
        { name: 'Green Lentils (dry)', qty: '1.5 cups', price: 2.49, used_in: 'Lentil Soup' },
        { name: 'Baby Carrots bag', qty: '1 lb', price: 2.49, used_in: 'Pot Roast' },
        { name: 'Kalamata Olives', qty: '½ cup', price: 1.99, used_in: 'Chicken Cacciatore' },
      ],
    },
    {
      section: '🧀 Dairy & Refrigerated',
      items: [
        { name: 'Shredded Tex-Mex Cheese', qty: '200g', price: 2.49, used_in: 'Ground Turkey Tacos' },
      ],
    },
  ],
};

export const WALMART_MEAL_PLAN: MealPlan = {
  store_name: 'Walmart',
  week_summary: '7 budget-smart meals from Walmart Canada\'s current flyer — whole chicken, pork chops, and pantry staples at rollback prices.',
  demo_mode: true,
  sale_items_found: 134,
  savings_vs_regular: 29.80,
  total_grocery_cost: 64.20,
  total_grocery_cost_no_spices: 52.70,
  meals: [
    {
      id: 'wmt-1',
      day: 'Monday', meal_type: 'Dinner',
      name: 'Rotisserie-Style Whole Roast Chicken',
      tagline: 'Whole chicken at Walmart\'s rollback price — feeds a family of 4',
      serves: 4, cook_time: '1 hr 30 min', method: 'Roast',
      total_cost: 12.00, per_serving: 3.00,
      ingredients: [
        { name: 'Whole Chicken (Walmart – rollback)', qty: '1.8 kg', price: 8.99, on_sale: true },
        { name: 'Paprika, garlic powder, onion powder', qty: 'pantry', price: 0.00, on_sale: false },
        { name: 'Butter', qty: '2 tbsp', price: 0.50, on_sale: false },
        { name: 'Lemon, rosemary', qty: 'pantry', price: 0.00, on_sale: false },
      ],
      instructions: [
        'Preheat oven to 425°F.',
        'Pat chicken dry. Rub all over with softened butter mixed with paprika, garlic, onion powder.',
        'Stuff cavity with halved lemon and rosemary sprigs.',
        'Roast 1 hr 15 min until juices run clear (165°F internal).',
        'Rest 10 min before carving.',
      ],
      tip: 'Save the bones and carcass to make homemade chicken stock.',
    },
    {
      id: 'wmt-2',
      day: 'Tuesday', meal_type: 'Dinner',
      name: 'Honey Garlic Pork Chops',
      tagline: 'Bone-in pork chops with sticky honey-garlic glaze — 20-minute meal',
      serves: 4, cook_time: '20 min', method: 'Stovetop',
      total_cost: 11.80, per_serving: 2.95,
      ingredients: [
        { name: 'Bone-In Pork Chops (Walmart – rollback)', qty: '4 chops (1 kg)', price: 7.98, on_sale: true },
        { name: 'Honey', qty: '3 tbsp', price: 0.90, on_sale: false },
        { name: 'Soy sauce, garlic, butter', qty: 'pantry', price: 0.00, on_sale: false },
        { name: 'Salt and pepper', qty: 'pantry', price: 0.00, on_sale: false },
      ],
      instructions: [
        'Season pork chops with salt and pepper.',
        'Sear in a hot pan with oil, 4 min per side.',
        'Reduce heat; add butter, minced garlic, honey, and soy sauce.',
        'Spoon glaze over chops for 2 min until sticky and caramelised.',
        'Rest 3 min before serving.',
      ],
      tip: 'Don\'t skip the resting step — pork chops tighten up while resting then relax back to juicy.',
    },
    {
      id: 'wmt-3',
      day: 'Wednesday', meal_type: 'Dinner',
      name: 'Beef Meatball Pasta',
      tagline: 'Extra-lean ground beef meatballs in marinara over spaghetti',
      serves: 5, cook_time: '35 min', method: 'Bake + Simmer',
      total_cost: 13.40, per_serving: 2.68,
      ingredients: [
        { name: 'Extra Lean Ground Beef (Walmart – rollback)', qty: '500g', price: 5.98, on_sale: true },
        { name: 'Spaghetti (900g box)', qty: '½ box', price: 1.49, on_sale: true },
        { name: 'Jarred Marinara Sauce', qty: '1 × 660ml jar', price: 2.98, on_sale: true },
        { name: 'Egg, breadcrumbs, garlic, Parmesan', qty: 'pantry', price: 0.00, on_sale: false },
      ],
      instructions: [
        'Mix ground beef with egg, breadcrumbs, garlic, and Parmesan.',
        'Roll into 18 golf ball-sized meatballs.',
        'Bake at 400°F for 18 min.',
        'Simmer meatballs in marinara sauce 10 min.',
        'Cook spaghetti per package. Serve meatballs over pasta.',
      ],
      tip: 'Baking the meatballs instead of frying them means less oil and a more evenly cooked interior.',
    },
    {
      id: 'wmt-4',
      day: 'Thursday', meal_type: 'Dinner',
      name: 'Frozen Breaded Fish & Fries',
      tagline: 'Walmart\'s Great Value fish fillets oven-baked — fish & chips at home',
      serves: 3, cook_time: '30 min', method: 'Bake',
      total_cost: 8.90, per_serving: 2.97,
      ingredients: [
        { name: 'Frozen Breaded Cod Fillets (Walmart Great Value)', qty: '600g box', price: 5.98, on_sale: true },
        { name: 'Frozen Fries (Walmart Great Value)', qty: '500g', price: 1.98, on_sale: true },
        { name: 'Malt vinegar, tartar sauce, lemon', qty: 'pantry', price: 0.00, on_sale: false },
      ],
      instructions: [
        'Preheat oven to 425°F.',
        'Arrange fish fillets and frozen fries on two baking sheets.',
        'Bake fish 22 min and fries 25 min, flipping halfway.',
        'Serve with tartar sauce, lemon wedges, and malt vinegar.',
      ],
      tip: 'Use two baking sheets so nothing steams — crispiness requires space and hot air circulation.',
    },
    {
      id: 'wmt-5',
      day: 'Friday', meal_type: 'Dinner',
      name: 'Chicken Breast Rice Bowl',
      tagline: 'Marinated chicken breast with rice and veggies — meal-prep king',
      serves: 4, cook_time: '30 min', method: 'Stovetop',
      total_cost: 11.50, per_serving: 2.88,
      ingredients: [
        { name: 'Boneless Skinless Chicken Breast (Walmart – rollback)', qty: '600g', price: 5.98, on_sale: true },
        { name: 'White Rice (2 kg bag)', qty: '2 cups dry', price: 1.00, on_sale: false },
        { name: 'Broccoli florets (frozen)', qty: '2 cups', price: 1.98, on_sale: true },
        { name: 'Teriyaki sauce, sesame oil', qty: 'pantry', price: 0.00, on_sale: false },
      ],
      instructions: [
        'Marinate chicken in teriyaki sauce 15 min.',
        'Cook chicken in sesame oil over medium heat, 6 min per side.',
        'Cook rice. Steam frozen broccoli.',
        'Slice chicken, serve over rice with broccoli.',
      ],
      tip: 'Make a double batch on Friday and use leftovers for Saturday lunch.',
    },
    {
      id: 'wmt-6',
      day: 'Saturday', meal_type: 'Lunch',
      name: 'Grilled Cheese & Tomato Soup',
      tagline: 'Walmart\'s PC brand canned tomato soup + thick grilled cheese — the classic',
      serves: 2, cook_time: '15 min', method: 'Stovetop',
      total_cost: 4.20, per_serving: 2.10,
      ingredients: [
        { name: 'Tomato Soup (can, 796ml)', qty: '1 can', price: 1.47, on_sale: true },
        { name: 'Sandwich Bread', qty: '4 slices', price: 0.80, on_sale: false },
        { name: 'Processed Cheese Slices (pkg)', qty: '4 slices', price: 1.29, on_sale: true },
        { name: 'Butter', qty: 'pantry', price: 0.00, on_sale: false },
      ],
      instructions: [
        'Prepare soup per can instructions with milk or water.',
        'Butter bread slices on outside. Add 2 cheese slices inside.',
        'Grill in pan over medium heat, 2–3 min per side until golden.',
        'Serve grilled cheese halved alongside hot soup.',
      ],
      tip: 'Add a pinch of smoked paprika to the soup for a subtle depth of flavour.',
    },
    {
      id: 'wmt-7',
      day: 'Sunday', meal_type: 'Dinner',
      name: 'Slow Cooker Pulled Pork Sandwiches',
      tagline: 'Boneless pork shoulder — set it and forget it all day, feast at dinner',
      serves: 6, cook_time: '8 hrs (slow cooker)', method: 'Slow Cooker',
      total_cost: 17.20, per_serving: 2.87,
      ingredients: [
        { name: 'Boneless Pork Shoulder (Walmart – rollback)', qty: '1.5 kg', price: 9.98, on_sale: true },
        { name: 'Hamburger Buns (pkg of 8)', qty: '6 buns', price: 2.47, on_sale: false },
        { name: 'BBQ Sauce (bottle)', qty: '1 bottle', price: 2.48, on_sale: true },
        { name: 'Coleslaw mix (bag)', qty: '1 bag', price: 1.98, on_sale: true },
      ],
      instructions: [
        'Rub pork shoulder with salt, pepper, smoked paprika, and garlic powder.',
        'Place in slow cooker with ½ cup BBQ sauce.',
        'Cook on LOW for 8 hours.',
        'Shred with two forks. Toss with remaining BBQ sauce.',
        'Serve on toasted buns with coleslaw on top.',
      ],
      tip: 'The coleslaw on top is essential — the cool crunch balances the rich warm pork perfectly.',
    },
  ],
  grocery_list: [
    {
      section: '🥩 Meat & Poultry',
      items: [
        { name: 'Whole Chicken 1.8 kg', qty: '1', price: 8.99, used_in: 'Roast Chicken' },
        { name: 'Bone-In Pork Chops', qty: '4 chops (1 kg)', price: 7.98, used_in: 'Honey Garlic Pork Chops' },
        { name: 'Extra Lean Ground Beef', qty: '500g', price: 5.98, used_in: 'Meatball Pasta' },
        { name: 'Boneless Skinless Chicken Breast', qty: '600g', price: 5.98, used_in: 'Chicken Rice Bowl' },
        { name: 'Boneless Pork Shoulder', qty: '1.5 kg', price: 9.98, used_in: 'Pulled Pork' },
      ],
    },
    {
      section: '🐟 Frozen',
      items: [
        { name: 'Breaded Cod Fillets', qty: '600g box', price: 5.98, used_in: 'Fish & Fries' },
        { name: 'Frozen Fries', qty: '500g', price: 1.98, used_in: 'Fish & Fries' },
        { name: 'Broccoli Florets (frozen)', qty: '300g', price: 1.98, used_in: 'Chicken Rice Bowl' },
      ],
    },
    {
      section: '🥫 Pantry',
      items: [
        { name: 'Spaghetti 900g box', qty: '½ box', price: 1.49, used_in: 'Meatball Pasta' },
        { name: 'Marinara Sauce 660ml', qty: '1 jar', price: 2.98, used_in: 'Meatball Pasta' },
        { name: 'Tomato Soup 796ml', qty: '1 can', price: 1.47, used_in: 'Grilled Cheese & Tomato Soup' },
        { name: 'BBQ Sauce bottle', qty: '1', price: 2.48, used_in: 'Pulled Pork' },
        { name: 'Hamburger Buns (8 pk)', qty: '1 pkg', price: 2.47, used_in: 'Pulled Pork Sandwiches' },
        { name: 'Coleslaw mix bag', qty: '1 bag', price: 1.98, used_in: 'Pulled Pork Sandwiches' },
      ],
    },
    {
      section: '🧀 Dairy',
      items: [
        { name: 'Processed Cheese Slices', qty: '1 pkg', price: 1.29, used_in: 'Grilled Cheese' },
      ],
    },
    {
      section: '🍞 Bakery',
      items: [
        { name: 'Sandwich Bread', qty: '1 loaf', price: 2.47, used_in: 'Grilled Cheese' },
      ],
    },
  ],
};

export const METRO_MEAL_PLAN: MealPlan = {
  store_name: 'Metro',
  week_summary: '7 meals from this week\'s Metro flyer — highlighting Ontario pork tenderloin, wild-caught shrimp, and a fresh pasta special.',
  demo_mode: true,
  sale_items_found: 76,
  savings_vs_regular: 27.50,
  total_grocery_cost: 68.90,
  total_grocery_cost_no_spices: 56.40,
  meals: [
    {
      id: 'metro-1',
      day: 'Monday', meal_type: 'Dinner',
      name: 'Garlic Butter Shrimp Pasta',
      tagline: 'Wild-caught shrimp tossed in garlicky butter with spaghetti',
      serves: 3, cook_time: '25 min', method: 'Stovetop',
      total_cost: 13.60, per_serving: 4.53,
      ingredients: [
        { name: 'Wild-Caught Shrimp, peeled (Metro – on sale)', qty: '400g', price: 7.99, on_sale: true },
        { name: 'Spaghetti', qty: '300g', price: 1.89, on_sale: false },
        { name: 'Butter', qty: '4 tbsp', price: 0.80, on_sale: false },
        { name: 'Garlic (5 cloves), parsley, lemon, chili flakes', qty: 'pantry', price: 0.00, on_sale: false },
      ],
      instructions: [
        'Cook spaghetti al dente, reserve ½ cup pasta water.',
        'Sauté shrimp in butter and garlic over medium-high, 2 min per side.',
        'Add pasta, pasta water, lemon juice, parsley, and chili flakes.',
        'Toss vigorously until sauce emulsifies and coats the pasta.',
      ],
      tip: 'The pasta water is key to making a creamy sauce without cream.',
    },
    {
      id: 'metro-2',
      day: 'Tuesday', meal_type: 'Dinner',
      name: 'Ontario Pork Tenderloin Medallions',
      tagline: 'Ontario pork on sale — quick sear with Dijon cream sauce',
      serves: 3, cook_time: '25 min', method: 'Stovetop',
      total_cost: 11.40, per_serving: 3.80,
      ingredients: [
        { name: 'Ontario Pork Tenderloin (Metro – on sale)', qty: '450g', price: 6.49, on_sale: true },
        { name: 'Heavy cream', qty: '¼ cup', price: 1.20, on_sale: false },
        { name: 'Dijon mustard, shallots, thyme', qty: 'pantry', price: 0.00, on_sale: false },
        { name: 'Baby potatoes (bag)', qty: '½ bag', price: 2.49, on_sale: true },
      ],
      instructions: [
        'Slice tenderloin into 2 cm medallions.',
        'Sear in butter, 2 min per side. Remove and rest.',
        'Cook shallots in same pan. Add Dijon, cream, and thyme.',
        'Simmer sauce 3 min. Return pork, coat and serve with boiled baby potatoes.',
      ],
      tip: 'Don\'t overcook pork — 145°F internal temperature is perfect (still slightly pink is fine).',
    },
    {
      id: 'metro-3',
      day: 'Wednesday', meal_type: 'Dinner',
      name: 'Chicken & Mushroom Risotto',
      tagline: 'Creamy Arborio rice with Metro chicken thighs and cremini mushrooms',
      serves: 4, cook_time: '45 min', method: 'Stovetop',
      total_cost: 14.20, per_serving: 3.55,
      ingredients: [
        { name: 'Boneless Chicken Thighs (Metro – on sale)', qty: '500g', price: 5.99, on_sale: true },
        { name: 'Arborio Rice', qty: '1.5 cups', price: 2.49, on_sale: false },
        { name: 'Cremini Mushrooms (pkg)', qty: '227g', price: 2.99, on_sale: true },
        { name: 'Parmesan, white wine, chicken broth, onion', qty: 'pantry', price: 0.00, on_sale: false },
      ],
      instructions: [
        'Season and sear chicken thighs. Set aside and slice.',
        'Sauté onion and mushrooms. Add Arborio rice, toast 1 min.',
        'Add white wine, then add warm chicken broth ladle by ladle, stirring.',
        'After 25 min, rice should be creamy. Stir in Parmesan.',
        'Top with sliced chicken.',
      ],
      tip: 'Keep the broth warm in a separate pot — adding cold liquid slows cooking and makes risotto gluey.',
    },
    {
      id: 'metro-4',
      day: 'Thursday', meal_type: 'Dinner',
      name: 'Sheet Pan Sausage & Peppers',
      tagline: 'Italian sausage from Metro\'s deli with tri-colour peppers — one pan dinner',
      serves: 4, cook_time: '35 min', method: 'Roast',
      total_cost: 10.80, per_serving: 2.70,
      ingredients: [
        { name: 'Italian Sausages (Metro deli – on sale)', qty: '4 sausages (500g)', price: 5.99, on_sale: true },
        { name: 'Bell Peppers (red, yellow, green)', qty: '3 peppers', price: 2.97, on_sale: true },
        { name: 'Red onion', qty: '1', price: 0.89, on_sale: false },
        { name: 'Italian seasoning, olive oil, balsamic', qty: 'pantry', price: 0.00, on_sale: false },
      ],
      instructions: [
        'Slice peppers and onion into strips.',
        'Toss with olive oil, balsamic, Italian seasoning, salt and pepper.',
        'Spread on sheet pan. Nestle sausages on top.',
        'Roast at 425°F for 30 min, turning once.',
        'Serve with crusty bread or hoagie rolls.',
      ],
      tip: 'Use a hot oven and don\'t crowd the pan so the vegetables caramelize rather than steam.',
    },
    {
      id: 'metro-5',
      day: 'Friday', meal_type: 'Dinner',
      name: 'Salmon & Asparagus Foil Pack',
      tagline: 'Salmon fillet and asparagus roasted together in foil — zero cleanup',
      serves: 2, cook_time: '25 min', method: 'Bake',
      total_cost: 11.60, per_serving: 5.80,
      ingredients: [
        { name: 'Salmon Fillet (Metro – on sale)', qty: '2 × 150g portions', price: 7.99, on_sale: true },
        { name: 'Asparagus bunch', qty: '1 bunch', price: 2.99, on_sale: true },
        { name: 'Lemon, butter, garlic, dill', qty: 'pantry', price: 0.00, on_sale: false },
      ],
      instructions: [
        'Preheat oven to 400°F.',
        'Lay asparagus on large foil sheet. Place salmon on top.',
        'Top with lemon slices, pats of butter, garlic, and dill.',
        'Seal foil into a tight packet.',
        'Bake 20–22 min. Open carefully (steam!). Serve in packet.',
      ],
      tip: 'The foil packet traps steam and bastes the salmon in its own juices — incredibly moist.',
    },
    {
      id: 'metro-6',
      day: 'Saturday', meal_type: 'Lunch',
      name: 'Classic Vegetable Minestrone',
      tagline: 'Loaded with Metro\'s on-sale seasonal vegetables — hearty and filling',
      serves: 6, cook_time: '40 min', method: 'Stovetop',
      total_cost: 9.40, per_serving: 1.57,
      ingredients: [
        { name: 'Zucchini', qty: '2', price: 1.89, on_sale: true },
        { name: 'Canned white kidney beans', qty: '1 can', price: 1.29, on_sale: false },
        { name: 'Canned crushed tomatoes', qty: '1 × 796ml', price: 1.99, on_sale: true },
        { name: 'Ditalini pasta', qty: '1 cup', price: 0.80, on_sale: false },
        { name: 'Onion, celery, carrot, garlic, vegetable broth', qty: 'pantry', price: 0.00, on_sale: false },
      ],
      instructions: [
        'Sauté diced onion, celery, carrot, and garlic.',
        'Add zucchini, crushed tomatoes, and beans.',
        'Add 6 cups vegetable broth. Simmer 20 min.',
        'Add pasta and cook 10 more min.',
        'Season with salt, pepper, and a drizzle of olive oil.',
      ],
      tip: 'Add a Parmesan rind to the pot while simmering for incredible depth of flavour.',
    },
    {
      id: 'metro-7',
      day: 'Sunday', meal_type: 'Dinner',
      name: 'Herb-Crusted Rack of Lamb',
      tagline: 'Special occasion lamb rack on sale — impressive but surprisingly simple',
      serves: 3, cook_time: '40 min', method: 'Sear + Roast',
      total_cost: 28.80, per_serving: 9.60,
      ingredients: [
        { name: 'Rack of Lamb (Metro – on sale)', qty: '1 rack (700g)', price: 22.99, on_sale: true },
        { name: 'Fresh breadcrumbs', qty: '¼ cup', price: 0.80, on_sale: false },
        { name: 'Dijon mustard', qty: '2 tbsp', price: 0.80, on_sale: false },
        { name: 'Rosemary, garlic, thyme, olive oil', qty: 'pantry', price: 0.00, on_sale: false },
      ],
      instructions: [
        'Preheat oven to 425°F.',
        'Frenched rack: sear fat side down in hot oven-safe pan, 2 min.',
        'Brush meat with Dijon. Press breadcrumbs mixed with herbs onto meat side.',
        'Roast 20–22 min for medium-rare (135°F internal).',
        'Rest 8 min. Slice into individual chops.',
      ],
      tip: 'Buy a frenched rack (bones already cleaned) — it\'s dramatically more impressive.',
    },
  ],
  grocery_list: [
    {
      section: '🐟 Seafood',
      items: [
        { name: 'Wild-Caught Shrimp, peeled', qty: '400g', price: 7.99, used_in: 'Garlic Butter Shrimp Pasta' },
        { name: 'Salmon Fillet portions', qty: '2 × 150g', price: 7.99, used_in: 'Salmon & Asparagus Foil Pack' },
      ],
    },
    {
      section: '🥩 Meat',
      items: [
        { name: 'Ontario Pork Tenderloin', qty: '450g', price: 6.49, used_in: 'Pork Medallions' },
        { name: 'Boneless Chicken Thighs', qty: '500g', price: 5.99, used_in: 'Chicken Mushroom Risotto' },
        { name: 'Italian Sausages', qty: '500g (4 links)', price: 5.99, used_in: 'Sheet Pan Sausage & Peppers' },
        { name: 'Rack of Lamb (frenched)', qty: '700g', price: 22.99, used_in: 'Herb-Crusted Rack of Lamb' },
      ],
    },
    {
      section: '🥦 Produce',
      items: [
        { name: 'Asparagus', qty: '1 bunch', price: 2.99, used_in: 'Salmon Foil Pack' },
        { name: 'Cremini Mushrooms', qty: '227g pkg', price: 2.99, used_in: 'Chicken Mushroom Risotto' },
        { name: 'Bell Peppers (3 colours)', qty: '3', price: 2.97, used_in: 'Sheet Pan Sausage' },
        { name: 'Baby Potatoes bag', qty: '½ bag', price: 2.49, used_in: 'Pork Medallions' },
        { name: 'Zucchini', qty: '2', price: 1.89, used_in: 'Minestrone' },
        { name: 'Red onion', qty: '1', price: 0.89, used_in: 'Sheet Pan Sausage' },
      ],
    },
    {
      section: '🥫 Pantry',
      items: [
        { name: 'Arborio Rice', qty: '500g bag', price: 2.49, used_in: 'Chicken Mushroom Risotto' },
        { name: 'Spaghetti', qty: '300g', price: 1.89, used_in: 'Shrimp Pasta' },
        { name: 'Canned white kidney beans', qty: '1 can', price: 1.29, used_in: 'Minestrone' },
        { name: 'Crushed Tomatoes 796ml', qty: '1 can', price: 1.99, used_in: 'Minestrone' },
      ],
    },
  ],
};

export const FRESHCO_MEAL_PLAN: MealPlan = {
  store_name: 'FreshCo',
  week_summary: '7 ultra-budget meals from FreshCo — Canada\'s lowest everyday prices with this week\'s specials on chicken drumsticks, eggs, and pantry staples.',
  demo_mode: true,
  sale_items_found: 112,
  savings_vs_regular: 22.40,
  total_grocery_cost: 49.80,
  total_grocery_cost_no_spices: 40.30,
  meals: [
    {
      id: 'fc-1',
      day: 'Monday', meal_type: 'Dinner',
      name: 'Crispy Baked Chicken Drumsticks',
      tagline: 'Drumsticks at FreshCo\'s low price — baked crispy with no frying',
      serves: 4, cook_time: '45 min', method: 'Bake',
      total_cost: 9.80, per_serving: 2.45,
      ingredients: [
        { name: 'Chicken Drumsticks (FreshCo – on sale)', qty: '8 drumsticks (1 kg)', price: 5.98, on_sale: true },
        { name: 'Paprika, garlic powder, onion powder', qty: 'pantry', price: 0.00, on_sale: false },
        { name: 'Vegetable oil', qty: '2 tbsp', price: 0.30, on_sale: false },
        { name: 'Frozen mixed veg (side)', qty: '2 cups', price: 1.49, on_sale: true },
      ],
      instructions: [
        'Preheat oven to 425°F.',
        'Toss drumsticks with oil, paprika, garlic powder, onion powder, salt.',
        'Place on wire rack over baking sheet.',
        'Bake 40 min, flipping once at 20 min.',
        'Steam frozen veg. Serve alongside.',
      ],
      tip: 'A wire rack lets hot air circulate under the drumsticks for all-around crispiness without deep-frying.',
    },
    {
      id: 'fc-2',
      day: 'Tuesday', meal_type: 'Dinner',
      name: 'Eggs & Vegetable Fried Rice',
      tagline: 'Eggs on sale + day-old rice = the best fried rice you\'ll make at home',
      serves: 3, cook_time: '15 min', method: 'Wok',
      total_cost: 4.80, per_serving: 1.60,
      ingredients: [
        { name: 'Eggs (FreshCo – on sale)', qty: '4 eggs', price: 1.20, on_sale: true },
        { name: 'Frozen peas & carrots mix', qty: '1 cup', price: 0.80, on_sale: false },
        { name: 'Cooked rice (day-old)', qty: '3 cups', price: 0.50, on_sale: false },
        { name: 'Soy sauce, sesame oil, green onions, garlic', qty: 'pantry', price: 0.00, on_sale: false },
      ],
      instructions: [
        'Heat oil in large pan or wok over high heat.',
        'Add frozen veg, stir-fry 2 min.',
        'Push to side. Scramble eggs in empty side.',
        'Add cold rice, soy sauce, and sesame oil. Toss everything.',
        'Garnish with sliced green onions.',
      ],
      tip: 'Day-old refrigerated rice is essential — fresh rice is too wet and turns mushy.',
    },
    {
      id: 'fc-3',
      day: 'Wednesday', meal_type: 'Dinner',
      name: 'Ground Beef Chili',
      tagline: 'Budget chili with canned beans and tomatoes — makes 6 hearty servings',
      serves: 6, cook_time: '45 min', method: 'Stovetop',
      total_cost: 11.40, per_serving: 1.90,
      ingredients: [
        { name: 'Ground Beef (FreshCo – on sale)', qty: '500g', price: 3.99, on_sale: true },
        { name: 'Kidney Beans (2 cans)', qty: '2 × 540ml', price: 2.38, on_sale: true },
        { name: 'Diced Tomatoes (can)', qty: '1 × 796ml', price: 1.29, on_sale: true },
        { name: 'Chili powder, cumin, onion, garlic, peppers', qty: 'pantry', price: 0.00, on_sale: false },
      ],
      instructions: [
        'Brown ground beef with diced onion and garlic.',
        'Add green pepper, chili powder, cumin, salt, and pepper.',
        'Add diced tomatoes and kidney beans.',
        'Simmer 35 min, stirring occasionally.',
        'Serve with cornbread or rice.',
      ],
      tip: 'Chili tastes better the next day — make a double batch and freeze half.',
    },
    {
      id: 'fc-4',
      day: 'Thursday', meal_type: 'Dinner',
      name: 'Pasta e Fagioli',
      tagline: 'Italian pasta and beans soup — ultra filling, under $2/serving',
      serves: 5, cook_time: '30 min', method: 'Stovetop',
      total_cost: 7.80, per_serving: 1.56,
      ingredients: [
        { name: 'Cannellini Beans (can)', qty: '1 × 540ml', price: 1.29, on_sale: false },
        { name: 'Ditalini or small pasta', qty: '1 cup', price: 0.80, on_sale: false },
        { name: 'Crushed Tomatoes (can)', qty: '1 × 400ml', price: 1.29, on_sale: true },
        { name: 'Rosemary, parmesan rind, garlic, celery, carrot', qty: 'pantry', price: 0.00, on_sale: false },
      ],
      instructions: [
        'Sauté minced garlic, diced celery, and carrot in olive oil.',
        'Add crushed tomatoes and beans. Simmer 10 min.',
        'Blend ¼ of the soup to thicken, return to pot.',
        'Add pasta and vegetable broth. Cook until pasta is tender.',
        'Finish with Parmesan and black pepper.',
      ],
      tip: 'Drop in a Parmesan rind while simmering — removes it before serving but leaves incredible umami depth.',
    },
    {
      id: 'fc-5',
      day: 'Friday', meal_type: 'Dinner',
      name: 'Pork Chop & Cabbage Stir-Fry',
      tagline: 'Pork chops and cabbage — budget and filling, done in 20 minutes',
      serves: 3, cook_time: '20 min', method: 'Stovetop',
      total_cost: 8.40, per_serving: 2.80,
      ingredients: [
        { name: 'Pork Loin Chops (FreshCo – on sale)', qty: '3 chops (500g)', price: 4.99, on_sale: true },
        { name: 'Green Cabbage (½ head)', qty: '½ head', price: 1.49, on_sale: true },
        { name: 'Soy sauce, rice vinegar, sesame oil, garlic', qty: 'pantry', price: 0.00, on_sale: false },
      ],
      instructions: [
        'Slice pork chops into thin strips.',
        'Stir-fry over high heat with garlic and sesame oil.',
        'Add shredded cabbage. Stir-fry 3 min until wilted.',
        'Add soy sauce and rice vinegar. Toss.',
        'Serve over steamed rice.',
      ],
      tip: 'High heat and fast cooking keeps the cabbage crisp and the pork tender.',
    },
    {
      id: 'fc-6',
      day: 'Saturday', meal_type: 'Breakfast',
      name: 'Veggie Omelette',
      tagline: 'FreshCo eggs on sale — loaded veggie omelette with whatever\'s in the fridge',
      serves: 2, cook_time: '15 min', method: 'Stovetop',
      total_cost: 3.80, per_serving: 1.90,
      ingredients: [
        { name: 'Eggs (FreshCo – on sale)', qty: '4 eggs', price: 1.20, on_sale: true },
        { name: 'Bell pepper, onion, spinach', qty: '½ each', price: 1.50, on_sale: false },
        { name: 'Shredded cheddar', qty: '¼ cup', price: 0.80, on_sale: false },
        { name: 'Butter, salt, pepper', qty: 'pantry', price: 0.00, on_sale: false },
      ],
      instructions: [
        'Sauté diced pepper and onion in butter until soft.',
        'Whisk eggs with salt, pepper, and splash of water.',
        'Pour eggs into pan over medium-low heat.',
        'As eggs set, add cheese and vegetables to one half.',
        'Fold and slide onto plate.',
      ],
      tip: 'Low and slow is the trick to a creamy, non-rubbery omelette.',
    },
    {
      id: 'fc-7',
      day: 'Sunday', meal_type: 'Dinner',
      name: 'Whole Chicken Noodle Soup',
      tagline: 'Stretch one whole FreshCo chicken into a giant pot of soup',
      serves: 8, cook_time: '2 hrs', method: 'Stovetop',
      total_cost: 12.20, per_serving: 1.53,
      ingredients: [
        { name: 'Whole Chicken (FreshCo – on sale)', qty: '1 whole chicken (1.8kg)', price: 7.98, on_sale: true },
        { name: 'Egg noodles', qty: '200g', price: 1.49, on_sale: false },
        { name: 'Celery, carrots, onion, garlic, bay leaves, thyme', qty: 'pantry', price: 0.00, on_sale: false },
      ],
      instructions: [
        'Simmer whole chicken in 3 litres of water with halved onion, garlic, celery, and carrots.',
        'After 1.5 hours, remove chicken and shred the meat.',
        'Strain broth and return to pot. Add egg noodles.',
        'Add shredded chicken back. Cook noodles 6 min.',
        'Season with salt and pepper. Serve with crusty bread.',
      ],
      tip: 'Homemade broth is 100× better than canned. This dish is the reason to own a whole chicken.',
    },
  ],
  grocery_list: [
    {
      section: '🥩 Meat',
      items: [
        { name: 'Chicken Drumsticks 1 kg', qty: '8 pieces', price: 5.98, used_in: 'Crispy Baked Chicken Drumsticks' },
        { name: 'Ground Beef 500g', qty: '500g', price: 3.99, used_in: 'Ground Beef Chili' },
        { name: 'Pork Loin Chops 500g', qty: '3 chops', price: 4.99, used_in: 'Pork Chop & Cabbage Stir-Fry' },
        { name: 'Whole Chicken 1.8 kg', qty: '1', price: 7.98, used_in: 'Chicken Noodle Soup' },
      ],
    },
    {
      section: '🥚 Eggs & Dairy',
      items: [
        { name: 'Eggs (dozen)', qty: '12 eggs', price: 3.99, used_in: 'Fried Rice, Omelette' },
        { name: 'Shredded Cheddar', qty: '200g', price: 2.49, used_in: 'Veggie Omelette' },
      ],
    },
    {
      section: '🥦 Produce',
      items: [
        { name: 'Green Cabbage', qty: '½ head', price: 1.49, used_in: 'Pork & Cabbage Stir-Fry' },
        { name: 'Frozen Peas & Carrots mix', qty: '300g bag', price: 1.29, used_in: 'Fried Rice' },
        { name: 'Frozen mixed veg', qty: '300g bag', price: 1.49, used_in: 'Chicken Drumsticks' },
      ],
    },
    {
      section: '🥫 Pantry',
      items: [
        { name: 'Kidney Beans (2 cans)', qty: '2 × 540ml', price: 2.38, used_in: 'Ground Beef Chili' },
        { name: 'Diced Tomatoes 796ml', qty: '1 can', price: 1.29, used_in: 'Chili' },
        { name: 'Crushed Tomatoes 400ml', qty: '1 can', price: 1.29, used_in: 'Pasta e Fagioli' },
        { name: 'Cannellini Beans 540ml', qty: '1 can', price: 1.29, used_in: 'Pasta e Fagioli' },
        { name: 'Ditalini pasta', qty: '500g bag', price: 1.49, used_in: 'Pasta e Fagioli' },
        { name: 'Egg noodles', qty: '200g', price: 1.49, used_in: 'Chicken Noodle Soup' },
      ],
    },
  ],
};

// ── Demo plan picker by chain name ──────────────────────────────────────────
export function getDemoMealPlan(chainName: string): MealPlan {
  const n = chainName.toLowerCase();
  if (n.includes('sobeys') || n.includes('iga') || n.includes('safeway') || n.includes('thrifty')) return SOBEYS_MEAL_PLAN;
  if (n.includes('walmart')) return WALMART_MEAL_PLAN;
  if (n.includes('metro') || n.includes('food basic') || n.includes('super c')) return METRO_MEAL_PLAN;
  if (n.includes('freshco') || n.includes('fresh co')) return FRESHCO_MEAL_PLAN;
  // Default Loblaws family is imported from the original demoMealPlan
  return SOBEYS_MEAL_PLAN; // fallback
}
